import { BrainLoadingIcon } from 'morpheus-ui';
import React, { useEffect, useRef, useState } from 'react';
import env from '../../../env';

interface SambaPlayerProps {
  token: string;
  loading?: boolean;
  virtualFinishAt?: number;
  onCreate?: (controls: any) => void;
  onStart?: () => void;
  onResume?: () => void;
  onFinish?: (origin?: 'virtual' | 'ended') => void;
  onVideoEnd?: () => void;
  onVirtualFinish?: () => void;
  /** Segundos assistidos, reportados a cada evento de progresso do player. */
  onProgressSeconds?: (seconds: number) => void;
  onError?: () => void;
  onListen?: (event: any) => void;
  id?: string;
  initialTime?: number;
  seekTo?: { seconds: number; requestId: number };
}

interface EventListener {
  onCreate?: (controls: any) => void;
  onLoad?: () => void;
  onStart?: () => void;
  onResume?: () => void;
  onFinish?: () => void;
  onError?: (event: any) => void;
  onListen?: (event: any) => void;
}

const buildPlayer = (id: string, token: string, eventListener: EventListener, initialTime = 0) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return new window.SambaPlayer(id, {
    height: '465px',
    width: '100%',
    ph: env.REACT_APP_SAMBA_PH,
    m: token,
    playerParams: {
      enableShare: false,
      startOutput: '360p',
      resume: initialTime === 0,
      initialTime: initialTime,
    },
    events: eventListener,
  });
};

const SambaPlayer: React.FC<SambaPlayerProps> = ({
                                                   loading,
                                                   token,
                                                   virtualFinishAt = 80,
                                                   onCreate,
                                                   onStart,
                                                   onResume,
                                                   onFinish,
                                                   onVideoEnd,
                                                   onVirtualFinish,
                                                   onProgressSeconds,
                                                   onError,
                                                   onListen,
                                                   id = 'samba-selector',
                                                   initialTime,
                                                   seekTo,
                                                 }) => {
  const [loaded, setLoaded] = useState(false);
  const [firedVirtualFinish, setFiredVirtualFinish] = useState(false);
  const [firedVideoEnd, setFiredVideoEnd] = useState(false);
  const [listenEvent, setLastListenEvent] = useState<any>({});
  const videoPlayerRef = useRef<HTMLDivElement>(null);
  const playerInstance = useRef<any>(null);
  const playerControls = useRef<any>(null);
  const appliedSeekRequest = useRef<number | null>(null);

  const eventListener: EventListener = {
    onCreate: controls => {
      playerControls.current = controls;
      onCreate && onCreate(controls);
    },
    onLoad: () => {
      setLoaded(true);
    },
    onStart: () => {
      onStart && onStart();
    },
    onResume: () => {
      onResume && onResume();
    },
    onFinish: () => {
      onFinish && onFinish('ended');
      onVideoEnd && onVideoEnd();
    },
    onError: event => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      onError && onError(event);
    },
    onListen: event => {
      setLastListenEvent(event);
      onListen && onListen(event);
    },
  };

  useEffect(() => {
    if (token && !loading) {
      if (!playerInstance.current) {
        playerInstance.current = buildPlayer(id, token, eventListener, initialTime);
        setLoaded(true);
      }
    }
  }, [token, loading, id, eventListener]);

  // O initialTime só é aplicado na criação do player, então saltos pedidos
  // depois (ex.: timeline da aula) precisam usar a API do player.
  useEffect(() => {
    if (!seekTo) {
      return;
    }
    const player = [playerInstance.current, playerControls.current].find(
      candidate => typeof candidate?.seek === 'function',
    );

    if (!player) {
      return;
    }
    if (appliedSeekRequest.current === seekTo.requestId) {
      return;
    }
    appliedSeekRequest.current = seekTo.requestId;
    player.seek(seekTo.seconds);
  }, [seekTo, loaded]);

  // `eventParam` é a posição em segundos; o player dispara isso enquanto o
  // vídeo corre. Quem consome decide a frequência de envio.
  useEffect(() => {
    if (onProgressSeconds && Number.isFinite(listenEvent.eventParam)) {
      onProgressSeconds(listenEvent.eventParam);
    }
  }, [listenEvent]);

  useEffect(() => {
    if (onFinish && virtualFinishAt && !firedVirtualFinish) {
      const percentage = listenEvent.eventParam / (listenEvent.duration / 1000);
      if (percentage * 100 > virtualFinishAt) {
        setFiredVirtualFinish(true);
        onFinish('virtual');
      }
    }
  }, [listenEvent, virtualFinishAt, firedVirtualFinish, onVirtualFinish]);

  useEffect(() => {
    if (onVideoEnd && !firedVideoEnd) {
      const percentage = listenEvent.eventParam / (listenEvent.duration / 1000);
      if (percentage * 100 > 99) {
        setFiredVideoEnd(true);
        onVideoEnd();
      }
    }
  }, [listenEvent, firedVideoEnd, onVideoEnd]);

  return (
    <div>
      <div ref={videoPlayerRef} id={id} className="w-full h-full">
        {!loaded &&
          <BrainLoadingIcon className='stroke-brand-color-primary-pure h-20 w-20 stroke-2 animate-pulse' />}
      </div>
    </div>
  );
};

export default SambaPlayer;
